import React, { ReactNode, useCallback, useState } from 'react';
import cn from 'classnames';
import Spinner from '@components/spinner';
import Typography from '@components/typography';
import { SpinnerProps } from 'interface';

import '@styles/button.scss';

interface ActionButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  disabled?: boolean;
  onClick?:
    | React.ComponentPropsWithoutRef<'button'>['onClick']
    | ((
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
      ) => Promise<unknown>);
  spinner?: ReactNode;
  spinnerProps?: SpinnerProps;
  colour?: 'primary' | 'primary-line' | 'alternative' | 'error';
  'data-cy': string;
  'data-testid': string;
  name: string;
}

/**
 * Single-click interactive button component
 * @param children Button node to render
 * @param disabled Flag to disable button
 * @param dataCy Cypress test name
 * @param dataTestid Unit test name
 * @param spinner Spinner node to render
 * @param spinnerProps Spinner properties
 * @param colour Button colour theme
 * @returns The action button component
 */
const ActionButton = ({
  children,
  disabled,
  onClick,
  spinner,
  spinnerProps,
  className,
  'data-cy': dataCy,
  'data-testid': dataTestid,
  name,
  ...etc
}: ActionButtonProps) => {
  const [inProgress, setInProgress] = useState<boolean>(false);

  const handleClick = useCallback(
    async (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ): Promise<unknown> => {
      setInProgress(true);
      try {
        if (!onClick) return;
        await onClick(event);
      } finally {
        setInProgress(false);
      }
    },
    [onClick, setInProgress]
  );

  const actualSpinner =
    inProgress && (spinner || <Spinner {...spinnerProps} />);

  return (
    <button
      name={name}
      role="button"
      type="button"
      data-testid={`action-button-${dataTestid}`}
      data-cy={`action-button-${dataCy}`}
      className={cn({
        'action-button': 'action-button',
        [`${className}`]: className,
      })}
      {...etc}
      disabled={disabled || inProgress}
      onClick={handleClick}
    >
      {actualSpinner}
      {!inProgress && (
        <Typography variant="button" size="rg" weight="semi-bold" {...etc}>
          {children}
        </Typography>
      )}
    </button>
  );
};

export default React.memo(ActionButton);
